import { css, html, LitElement } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import type {
    Commerce,
    CommerceStorefrontContact,
    CommerceShareResource,
    CommerceStorefrontExperience,
} from "@teqtank/commerce-sdk";
import "../share/teqtank-qr.js";

@customElement("teqtank-contact-card")
export class TeqtankContactCard extends LitElement {
    @property({ attribute: false }) public accessor commerce: Commerce | undefined;
    @property({ attribute: false }) public accessor contact: CommerceStorefrontContact | undefined;
    @property({ type: Boolean, attribute: "show-qr" }) public accessor showQr = false;
    @property() public accessor actions = "message email phone share save";
    @property() public accessor variant:
        "hero" | "compact" | "inline" | "sidebar" | "checkout" | "footer" | "floating" = "hero";

    @state()
    private accessor contactShare: CommerceShareResource | undefined;

    @state()
    private accessor storefront: CommerceStorefrontExperience | undefined;

    private unsubscribe: (() => void) | undefined;

    public static override styles = css`
        :host {
            container-type: inline-size;
            display: block;
            font-family: var(--teqtank-font-body, Inter, ui-sans-serif, system-ui, sans-serif);
        }
        .card {
            display: grid;
            gap: clamp(1rem, 3cqi, 1.4rem);
            padding: clamp(1.15rem, 4cqi, 1.75rem);
            border: 1px solid
                color-mix(
                    in srgb,
                    var(--teqtank-color-accent, #173f35) 16%,
                    var(--teqtank-color-border, #ddd7cc)
                );
            border-radius: var(--teqtank-contact-card-radius, 1.25rem);
            background: var(
                --teqtank-contact-card-background,
                linear-gradient(
                    150deg,
                    var(--teqtank-color-surface, #fff),
                    var(--teqtank-color-surface-soft, #f5f1e9)
                )
            );
            box-shadow: 0 1.35rem 3.25rem rgb(27 38 31 / 9%);
        }
        .top {
            display: flex;
            align-items: center;
            gap: 1rem;
        }
        .avatar {
            display: grid;
            place-items: center;
            width: 4rem;
            height: 4rem;
            border-radius: 50%;
            background: var(--teqtank-color-surface-soft, #f3efe7);
            background-color: var(--teqtank-color-accent, #173f35);
            color: var(--teqtank-color-on-accent, #fff);
            font-size: 1.25rem;
            font-weight: 800;
            overflow: hidden;
        }
        .avatar img {
            width: 100%;
            height: 100%;
            object-fit: cover;
        }
        h3,
        p {
            margin: 0;
        }
        .role,
        .bio,
        .location {
            color: var(--teqtank-color-text-muted, #68645d);
        }
        .location {
            margin-top: 0.2rem;
            font-size: 0.86rem;
        }
        .actions {
            display: flex;
            flex-wrap: wrap;
            gap: 0.6rem;
        }
        a,
        button {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            padding: 0.7rem 1rem;
            border: 1px solid var(--teqtank-color-border, #ccc);
            border-radius: 999px;
            background: transparent;
            color: inherit;
            font: inherit;
            font-weight: 700;
            text-decoration: none;
            cursor: pointer;
        }
        a:hover,
        button:hover {
            border-color: var(--teqtank-color-accent, #01676b);
            transform: translateY(-1px);
        }
        a:focus-visible,
        button:focus-visible {
            outline: 3px solid
                color-mix(in srgb, var(--teqtank-color-accent, #01676b) 30%, transparent);
            outline-offset: 2px;
        }
        .primary {
            background: var(--teqtank-color-accent, #01676b);
            border-color: var(--teqtank-color-accent, #01676b);
            color: #fff;
        }
        .qr {
            display: grid;
            place-items: center;
            padding-top: 0.5rem;
        }
        .compact,
        .inline,
        .checkout,
        .footer {
            padding: 0.85rem;
        }
        .compact .bio,
        .inline .bio,
        .checkout .bio,
        .footer .bio {
            display: none;
        }
        .compact .avatar,
        .inline .avatar,
        .checkout .avatar,
        .footer .avatar {
            width: 3rem;
            height: 3rem;
            font-size: 1rem;
        }
        .inline,
        .footer {
            grid-template-columns: minmax(0, 1fr) auto;
            align-items: center;
        }
        .inline .actions,
        .footer .actions {
            justify-content: flex-end;
        }
        .floating {
            box-shadow: 0 1rem 2.5rem rgb(0 0 0 / 12%);
        }
        @container (max-width: 620px) {
            .inline,
            .footer {
                grid-template-columns: 1fr;
            }
            .inline .actions,
            .footer .actions {
                justify-content: flex-start;
            }
        }
        @media (prefers-reduced-motion: reduce) {
            a,
            button {
                transition: none;
            }
        }
    `;

    public override connectedCallback(): void {
        super.connectedCallback();

        this.bind();
    }

    protected override updated(changed: Map<PropertyKey, unknown>): void {
        if (changed.has("commerce")) {
            this.bind();
        }
    }

    public override disconnectedCallback(): void {
        this.unsubscribe?.();

        super.disconnectedCallback();
    }

    protected override render() {
        const contact =
            this.contact ?? this.storefront?.contact ?? this.commerce?.storefront.current.contact;

        if (!contact) return html``;
        const initials = contact.displayName
            .split(/\s+/)
            .slice(0, 2)
            .map((v) => v[0])
            .join("")
            .toUpperCase();
        const actions = new Set(this.actions.split(/[\s,]+/u));
        return html`<article class="card ${this.variant}" part="card ${this.variant}">
            <div class="top" part="header">
                <span class="avatar" part="avatar"
                    >${contact.avatar ? html`<img part="avatar-image" src=${contact.avatar.url} alt=${contact.avatar.alternativeText} />` : initials}</span
                >
                <div part="identity">
                    <h3 part="name">${contact.displayName}</h3>
                    <p class="role" part="role">${contact.role}</p>
                    ${contact.location?.display ? html`<p class="location" part="location">${contact.location.display}</p>` : null}
                </div>
            </div>
            <p class="bio" part="biography">${contact.biography}</p>
            <div class="actions" part="actions">
                ${
                    actions.has("message") && contact.methods.messageUrl
                        ? html`<a
                              class="primary"
                              part="action primary-action message-action"
                              href=${contact.methods.messageUrl}
                              >Message</a
                          >`
                        : null
                }${
                    actions.has("email") && contact.methods.email
                        ? html`<a
                              part="action email-action"
                              href=${`mailto:${contact.methods.email}`}
                              >Email</a
                          >`
                        : null
                }${
                    actions.has("phone") && contact.methods.phone
                        ? html`<a part="action phone-action" href=${`tel:${contact.methods.phone}`}
                              >Call</a
                          >`
                        : null
                }${actions.has("share") ? html`<button part="action share-action" type="button" @click=${this.share}>Share contact</button>` : null}${
                    actions.has("save") && contact.methods.vCardUrl
                        ? html`<a part="action save-action" href=${contact.methods.vCardUrl}
                              >Save contact</a
                          >`
                        : null
                }
            </div>
            ${
                this.showQr && this.contactShare
                    ? html`<div class="qr" part="qr">
                          <teqtank-qr
                              part="qr-code"
                              .src=${this.contactShare.qrCodeUrl}
                              .value=${this.contactShare.url}
                              label="Scan to save or share contact"
                          ></teqtank-qr>
                      </div>`
                    : null
            }
        </article>`;
    }
    private async share(): Promise<void> {
        if (!this.commerce) return;
        this.contactShare = await this.commerce.share.contact();
        if (typeof navigator !== "undefined" && navigator.share) {
            await navigator
                .share({
                    title: this.contactShare.title,
                    text: this.contactShare.text,
                    url: this.contactShare.url,
                })
                .catch(() => undefined);
        }
        this.dispatchEvent(
            new CustomEvent("teqtank-share-created", {
                detail: this.contactShare,
                bubbles: true,
                composed: true,
            }),
        );
    }

    private bind(): void {
        this.unsubscribe?.();
        this.unsubscribe = undefined;

        if (!this.commerce) {
            return;
        }

        this.storefront = this.commerce.storefront.current;

        this.unsubscribe = this.commerce.storefront.onChanged((event) => {
            this.storefront = event.storefront;
        });
    }
}
declare global {
    interface HTMLElementTagNameMap {
        "teqtank-contact-card": TeqtankContactCard;
    }
}
